import { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { ActionBar } from "../../../design-system/patterns/ActionBar";
import { WorkspaceFrame, type WorkspaceStep } from "../../../design-system/patterns/WorkspaceFrame";
import { acuteAbdomenCard } from "../../../content/clinical-cards/acute-abdomen";
import type { ContentBlock } from "../../../domain/content-schema";
import { repairLegacyEncoding } from "../../../platform/content/repairLegacyEncoding";
import { ClinicalBlockRenderer } from "../components/ClinicalBlockRenderer";
import styles from "./AcuteAbdomenWorkspace.module.css";

type Section = Readonly<{ id: string; label: string; blocks: readonly ContentBlock[] }>;

function buildSections(blocks: readonly ContentBlock[]): readonly Section[] {
  const sections: { id: string; label: string; blocks: ContentBlock[] }[] = [];
  for (const block of blocks) {
    if (block.type === "heading" && block.level === 2) {
      sections.push({ id: block.anchor, label: repairLegacyEncoding(block.text), blocks: [block] });
    } else {
      sections.at(-1)?.blocks.push(block);
    }
  }
  return sections;
}

const sections = buildSections(acuteAbdomenCard.blocks);
const steps: readonly WorkspaceStep[] = sections.map((section) => ({
  id: section.id,
  label: section.label,
  shortLabel: section.id === "otuz-saniye" ? "30 saniye" : section.id === "ilk-bes-dakika" ? "İlk 5 dakika" : section.id === "ayirici" ? "Hızlı ayırım" : section.id === "tuzaklar" ? "Güvenlik" : section.label,
}));

export function AcuteAbdomenWorkspace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("bolum");
  const complete = requested === "tumu";
  const activeId = complete || sections.some((section) => section.id === requested) ? (requested ?? sections[0]?.id ?? "") : (sections[0]?.id ?? "");
  const activeIndex = Math.max(0, sections.findIndex((section) => section.id === activeId));
  const active = sections[activeIndex];
  const headingRef = useRef<HTMLHeadingElement>(null);
  const lastPanelRef = useRef(sections[0]?.id ?? "");
  const rendered = useMemo(() => complete ? sections : active ? [active] : [], [active, complete]);

  useEffect(() => {
    document.title = "Akut Karın: Cerrahi Acilleri Kaçırma | PedEmUP";
    if (requested && requested !== activeId && !complete) setSearchParams({ bolum: activeId }, { replace: true });
  }, [activeId, complete, requested, setSearchParams]);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, [activeId, complete]);

  useEffect(() => {
    if (!complete) lastPanelRef.current = activeId;
  }, [activeId, complete]);

  const select = (id: string) => setSearchParams({ bolum: id });
  const move = (offset: number) => {
    const target = sections[activeIndex + offset];
    if (target) select(target.id);
  };

  return (
    <WorkspaceFrame
      eyebrow="Klinik Kart · hızlı karar ve güvenlik"
      title={repairLegacyEncoding(acuteAbdomenCard.identity.title)}
      summary={repairLegacyEncoding(acuteAbdomenCard.identity.summary)}
      steps={steps}
      activeId={complete ? "" : activeId}
      onSelect={select}
      actionBar={<ActionBar canBack={!complete && activeIndex > 0} canNext={!complete && activeIndex < sections.length - 1} complete={complete} onBack={() => move(-1)} onNext={() => move(1)} onReadAll={() => setSearchParams({ bolum: complete ? lastPanelRef.current : "tumu" })} />}
      viewMode={complete ? "complete" : "step"}
    >
      <div className={complete ? styles.complete : styles.single}>
        {complete && <div className={styles.completeHeading}><p>Tam okuma görünümü</p><h2 ref={headingRef} tabIndex={-1}>Tüm klinik kart</h2><span>Yazdırma ve kapsamlı kaynak incelemesi için bütün bölümler açık gösterilir.</span></div>}
        {rendered.map((section, index) => <section className={styles.section} key={section.id} aria-labelledby={`section-${section.id}`}>
          {section.blocks.map((block, blockIndex) => block.type === "heading"
            ? <h2 id={`section-${section.id}`} ref={!complete && index === 0 ? headingRef : undefined} tabIndex={-1} key={`${section.id}-${blockIndex}`}>{repairLegacyEncoding(block.text)}</h2>
            : <ClinicalBlockRenderer key={`${section.id}-${blockIndex}`} block={block} references={acuteAbdomenCard.references} />)}
        </section>)}
      </div>
    </WorkspaceFrame>
  );
}
